import React, { useState, useRef } from 'react';
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import MultiSelectOptionsList from './MultiSelectOptionList';
import useClickOutside from '../../../hooks/useClickOutside';

export interface CustomSelectsOption {
    value: string;
    id: number | string;
    disabled?: boolean;
}

export interface ISelectedItem {
    value: string;
    label: string;
    id?: number;
}

export type SelectVariant = 'outline-orange' | 'outline-green' | 'outline-dark' | 'outline-teal' | 'outline-black' | 'orange';

interface VariantStyles {
    bg: string;
    selectedBg?: string;
    selectedText: string;
    placeholderText: string;
    border: string;
    selectedBorder?: string;
    hasBorder?: boolean;
    label: string;
    icon: string;
    hover: string;
}

const variantStyles: Record<SelectVariant, VariantStyles> = {
    'outline-orange': {
        bg: '',
        selectedBg: 'bg-brand-orange-10',
        selectedText: 'text-brand-orange-90',
        placeholderText: 'text-system-light-40',
        border: 'border-brand-orange-30',
        selectedBorder: 'border-brand-orange-50',
        hasBorder: true,
        label: 'text-system-light-40',
        icon: 'text-brand-orange-50 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:brightness-110',
    },
    'outline-green': {
        bg: 'bg-primary-100',
        selectedBg: 'bg-brand-green-5',
        selectedText: 'text-secondary-10',
        placeholderText: 'text-system-light-40',
        border: 'border-brand-green-30',
        selectedBorder: 'border-brand-green-20',
        hasBorder: true,
        label: 'text-system-light-40',
        icon: 'text-brand-green-50 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual hover:brightness-95 duration-200',
    },
    'outline-dark': {
        bg: 'transparent',
        selectedBg: 'transparent',
        selectedText: 'text-system-light-40',
        placeholderText: 'text-system-light-40',
        border: 'border-system-light-50',
        selectedBorder: 'border-system-light-50',
        hasBorder: true,
        label: 'text-system-light-40',
        icon: 'text-brand-green-50 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual hover:border-system-light-70 duration-200',
    },
    'outline-black': {
        bg: 'bg-primary-100',
        selectedBg: 'bg-brand-green-5',
        selectedText: 'text-primary-10',
        placeholderText: 'text-system-light-40',
        border: 'text-primary-0',
        selectedBorder: 'text-gray-0',
        hasBorder: true,
        label: 'text-primary-10 !text-tsmall',
        icon: 'text-brand-green-50 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual hover:brightness-95 duration-200',
    },
    'outline-teal': {
        bg: 'transparent',
        selectedBg: 'transparent',
        selectedText: 'text-primary-100',
        placeholderText: 'text-primary-99',
        border: 'border-brand-green-10',
        selectedBorder: 'border-brand-green-10',
        hasBorder: true,
        label: 'text-primary-100',
        icon: 'text-primary-100 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual duration-200'
    },
    orange: {
        bg: 'bg-brand-orange-20',
        selectedBg: 'bg-brand-orange-20',
        selectedText: 'text-primary-100',
        placeholderText: 'text-primary-100',
        border: 'border-transparent',
        hasBorder: false,
        label: 'text-system-light-40',
        icon: 'text-primary-100 w-[1.125rem] h-[1.125rem]',
        hover: 'hover:shadow-custom-dual hover:brightness-90 duration-200',
    },
};

export interface ICustomSelects {
    options: CustomSelectsOption[];
    onSelect: (selectedItems: ISelectedItem) => void;
    selectedItems: ISelectedItem[];
    label: string;
    disabled?: boolean;
    className?: string;
    classNameInput?: string;
    isRequired?: boolean;
    variant?: SelectVariant;
}

const SELECT_ALL_VALUES = ['Todos'];

function MultiSelectCheck({
    options,
    onSelect,
    selectedItems,
    label,
    disabled,
    className,
    classNameInput = '',
    isRequired,
    variant = 'orange',
    ...rest
}: ICustomSelects) {
    const [isOpen, setIsOpen] = useState(false);
    const CustomSelectsRef = useRef<HTMLDivElement>(null);

    const styles = variantStyles[variant];
    const hoverClass = disabled ? '' : styles.hover;

    useClickOutside(CustomSelectsRef, () => setIsOpen(false))

    const handleClick = (option: CustomSelectsOption) => {
        let selectOptions: ISelectedItem = {
            label: label,
            value: option.value,
            ...(option.id !== undefined && { id: Number(option.id) }),
        };
        onSelect(selectOptions);
    };

    const openCustomSelects = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const hasSelection = selectedItems.length > 0;
    const currentBg = hasSelection ? (styles.selectedBg ?? styles.bg) : styles.bg;
    const currentBorder = hasSelection ? (styles.selectedBorder ?? styles.border) : styles.border;

    return (
        <div
            ref={CustomSelectsRef}
            className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            {...rest}
        >
            {label &&
                <p className={`${styles.label} text-lmedium ps-2 pb-1`}>
                    {label}
                    {isRequired && <span className="text-error-40 ml-1">*</span>}
                </p>
            }
            <div
                className={`${classNameInput} h-[32px] flex items-center justify-between gap-1 md:gap-2 px-2 py-1 transition-[filter,background-color,border-color,box-shadow] ease-[cubic-bezier(0.2,0,0,1)] duration-200 rounded-sm ${hoverClass} ${styles.hasBorder
                    ? `border ${currentBorder} ${currentBg}`
                    : currentBg} 
                    ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                tabIndex={isOpen ? 0 : -1}
                aria-labelledby={`${label}-label`}
                aria-expanded={isOpen}
                onClick={openCustomSelects}
            >
                <div className='flex gap-1 h-full items-center w-[85%]'>
                    {hasSelection ? (
                        <div className='flex items-center gap-1 min-w-0'>
                            <span className={`whitespace-nowrap text-llarge truncate ${styles.selectedText}`}>
                                {selectedItems.some(item => item.value === 'Todos') ? 'Todos' : selectedItems[0].value}
                            </span>

                            {selectedItems.length > 1 &&
                                !selectedItems.some(item => SELECT_ALL_VALUES.includes(item.value)) && (
                                    <span className='bg-primary-10 text-lsmall text-indicator-null rounded-lg ml-2 px-2 py-[2px]'>
                                        +{selectedItems.length - 1}
                                    </span>
                                )}
                        </div>
                    ) : (
                        <span className={`truncate w-full text-llarge ${styles.placeholderText}`}>Seleccionar</span>
                    )}
                </div>
                <button
                    onClick={(e) => { e.stopPropagation(); openCustomSelects(); }}
                >
                    {
                        isOpen
                            ? <IoMdArrowDropup className={styles.icon} />
                            : <IoMdArrowDropdown className={styles.icon} />
                    }
                </button>
            </div>

            {isOpen && (
                <MultiSelectOptionsList
                    options={options}
                    selectedItems={selectedItems}
                    onSelect={handleClick}
                />
            )}
        </div>
    );
}

export default MultiSelectCheck;